import { ApiExplorerObjectInput } from '@squareup/dex-types-shared-api-explorer';
import { Box } from '@squareup/dex-ui-shared-base';
import {
  getDateTimeFormat,
  getFieldTypeForProperty,
} from '@squareup/dex-utils-shared-api-explorer';
import React, { FC, memo } from 'react';

import { ApiExplorerAnyField } from './ApiExplorerAnyField';
import { ApiExplorerArrayField } from './ApiExplorerArrayField';
import { ApiExplorerCollapsibleFieldLabel } from './ApiExplorerCollapsibleFieldLabel';
import { ApiExplorerEnumField } from './ApiExplorerEnumField';
import { ApiExplorerFieldLabel } from './ApiExplorerFieldLabel';
import { ApiExplorerMapField } from './ApiExplorerMapField';
import { ApiExplorerObjectField } from './ApiExplorerObjectField';
import { ApiExplorerPrimitiveField } from './ApiExplorerPrimitiveField';
import { ApiExplorerSchemaField } from './ApiExplorerSchemaField';
import { ApiExplorerFieldProps } from './props';

interface Props extends Omit<ApiExplorerFieldProps, 'currentValue'> {
  // This current value must always be an object. This is because we are
  // re-creating the appropriate input object based on the property name at this level
  currentValue: ApiExplorerObjectInput;
}

const ApiExplorerField: FC<Props> = memo(
  ({ depth = 0, property, currentValue, path, onChange }) => {
    const fieldType = getFieldTypeForProperty(property);

    const currentFieldValue = currentValue[property.name];
    const newPath = path ? `${path}/${property.name}` : property.name;

    const fieldNode = (function getFieldNode() {
      switch (fieldType) {
        case 'enum':
          return (
            <>
              <ApiExplorerFieldLabel property={property} />
              <ApiExplorerEnumField
                depth={depth}
                // This check does already happen in `getFieldTypeForProperty` but Typescript
                // has no available guard for this
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                entity={property.entity!}
                currentValue={currentFieldValue}
                onChange={onChange}
                path={newPath}
                name={property.name}
              />
            </>
          );
        case 'any':
          return (
            <>
              <ApiExplorerFieldLabel property={property} />
              <ApiExplorerAnyField
                depth={depth}
                property={property}
                currentValue={currentFieldValue}
                onChange={onChange}
                path={newPath}
              />
            </>
          );
        case 'object':
          return (
            <ApiExplorerCollapsibleFieldLabel
              property={property}
              currentValue={currentFieldValue}
              path={newPath}
              depth={depth}
              onChange={onChange}
            >
              <ApiExplorerObjectField
                depth={depth}
                // This check does already happen in `getFieldTypeForProperty` but Typescript
                // has no available guard for this
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                entity={property.entity!}
                currentValue={currentFieldValue}
                onChange={onChange}
                path={newPath}
              />
            </ApiExplorerCollapsibleFieldLabel>
          );
        case 'schema':
          return (
            <>
              <ApiExplorerFieldLabel property={property} />
              <ApiExplorerSchemaField
                depth={depth}
                property={property}
                currentValue={currentFieldValue}
                onChange={onChange}
                path={newPath}
              />
            </>
          );
        case 'array':
          return (
            <ApiExplorerCollapsibleFieldLabel
              depth={depth}
              property={property}
              onChange={onChange}
              currentValue={currentFieldValue}
              path={newPath}
            >
              <ApiExplorerArrayField
                depth={depth}
                property={property}
                currentValue={currentFieldValue}
                onChange={onChange}
                path={newPath}
              />
            </ApiExplorerCollapsibleFieldLabel>
          );
        case 'map':
          return (
            <ApiExplorerCollapsibleFieldLabel
              depth={depth}
              property={property}
              currentValue={currentFieldValue}
              onChange={onChange}
              path={newPath}
            >
              <ApiExplorerMapField
                depth={depth}
                property={property}
                currentValue={currentFieldValue}
                onChange={onChange}
                path={newPath}
              />
            </ApiExplorerCollapsibleFieldLabel>
          );
        case 'primitive':
        default:
          return (
            <>
              <ApiExplorerFieldLabel property={property} />
              <ApiExplorerPrimitiveField
                depth={depth}
                name={property.name}
                type={property.type}
                currentValue={currentFieldValue}
                onChange={onChange}
                path={newPath}
                datetimeFormat={getDateTimeFormat(property)}
              />
            </>
          );
      }
    })();

    return <Box margin={{ top: '2x' }}>{fieldNode}</Box>;
  }
);

ApiExplorerField.displayName = 'ApiExplorerField';

export { ApiExplorerField };
